import React, { useState, useMemo } from 'react';
import '../css/AddService.css';

const AddServiceModal = ({ theme, isOpen, onClose, onSubmit, vehicles, serviceContacts }) => {
    const [formData, setFormData] = useState({
        selectedVehicle: '',
        serviceTypes: [],
        selectedServiceContract: '',
    });

    const [regSearch, setRegSearch] = useState('');
    const [showVehicleDropdown, setShowVehicleDropdown] = useState(false);
    const [errors, setErrors] = useState('');

    const sortNaturally = (arr) => arr.slice().sort((a, b) =>
        a.reg.localeCompare(b.reg, undefined, { numeric: true, sensitivity: 'base' })
    );

    const groupedVehicles = useMemo(() => {
        const units    = sortNaturally(vehicles.filter(v => v.type === 'UNIT'));
        const rigids   = sortNaturally(vehicles.filter(v => v.type === 'RIGID'));
        const allT     = vehicles.filter(v => v.type === 'TRAILER');
        const moffett  = allT.filter(v => v.reg.slice(-1) === 'M');
        const ddeck    = allT.filter(v => v.reg.slice(-1) === 'D');
        const other    = allT.filter(v => v.reg.slice(-1) !== 'M' && v.reg.slice(-1) !== 'D');
        const trailers = [...sortNaturally(other), ...sortNaturally(moffett), ...sortNaturally(ddeck)];
        const mhe      = sortNaturally(vehicles.filter(v => v.type === 'MHE'));
        return [
            { label: 'Units',    items: units    },
            { label: 'Rigids',   items: rigids   },
            { label: 'Trailers', items: trailers },
            { label: 'MHE',      items: mhe      },
        ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicles]);

    const filteredGroups = groupedVehicles.map(group => ({
        ...group,
        items: regSearch
            ? group.items.filter(v => v.reg.toLowerCase().includes(regSearch.toLowerCase()))
            : group.items,
    })).filter(group => group.items.length > 0);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleCheckboxChange = (serviceType) => {
        setFormData((prev) => ({
            ...prev,
            serviceTypes: prev.serviceTypes.includes(serviceType)
                ? prev.serviceTypes.filter((type) => type !== serviceType)
                : [...prev.serviceTypes, serviceType],
        }));
    };

    const validateForm = () => {
        let errorMessage = '';
    
        if (!formData.selectedVehicle) {
            errorMessage = 'Please select a vehicle.';
        } else if (formData.serviceTypes.length !== 1) {
            errorMessage = 'Please select at one service type.';
        } else if (!formData.selectedServiceContract) {
            errorMessage = 'Please select a service contract.';
        }
    
        setErrors(errorMessage);
    
        // Return true if the form is valid (no errors)
        return !errorMessage;
    };
    

    const handleSubmit = () => {
        if (validateForm()) {
            onSubmit(formData);
            setRegSearch('');
            setFormData({ selectedVehicle: '', serviceTypes: [], selectedServiceContract: '' });
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div id={theme} className="overlay">
            <div className="modal-container">
                <h2>Add New Service</h2>
                <form className="form">

                    {/* Select Vehicle */}
                    <div className="input-group">
                        <label>Vehicle</label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type="text"
                                className="input"
                                placeholder="Search reg..."
                                value={regSearch}
                                autoComplete="off"
                                onChange={e => {
                                    setRegSearch(e.target.value);
                                    setFormData(prev => ({ ...prev, selectedVehicle: '' }));
                                    setShowVehicleDropdown(true);
                                }}
                                onFocus={() => setShowVehicleDropdown(true)}
                                onBlur={() => setTimeout(() => setShowVehicleDropdown(false), 150)}
                                style={{
                                    borderColor: formData.selectedVehicle ? '#0588f3' : undefined,
                                }}
                            />
                            {showVehicleDropdown && (
                                <ul style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    right: 0,
                                    maxHeight: 220,
                                    overflowY: 'auto',
                                    margin: 0,
                                    padding: 0,
                                    listStyle: 'none',
                                    border: '1px solid #ccc',
                                    borderRadius: 4,
                                    backgroundColor: theme === 'dark' ? '#1e2535' : '#fff',
                                    zIndex: 999,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                }}>
                                    {filteredGroups.length === 0 ? (
                                        <li style={{ padding: '8px 12px', color: '#999', fontSize: 13 }}>No matches</li>
                                    ) : filteredGroups.map((group, gi) => (
                                        <React.Fragment key={group.label}>
                                            <li style={{
                                                padding: '4px 0',
                                                textAlign: 'center',
                                                fontSize: 11,
                                                fontWeight: 600,
                                                letterSpacing: '0.05em',
                                                color: theme === 'dark' ? '#888' : '#aaa',
                                                userSelect: 'none',
                                                borderTop: gi > 0 ? '1px solid rgba(128,128,128,0.2)' : undefined,
                                                marginTop: gi > 0 ? 4 : 0,
                                            }}>
                                                <span style={{ margin: '0 6px' }}>{group.label}</span>
                                            </li>
                                            {group.items.map(vehicle => (
                                                <li
                                                    key={vehicle.id}
                                                    onMouseDown={() => {
                                                        setFormData(prev => ({ ...prev, selectedVehicle: vehicle.id }));
                                                        setRegSearch(vehicle.reg);
                                                        setShowVehicleDropdown(false);
                                                    }}
                                                    style={{
                                                        padding: '8px 12px',
                                                        cursor: 'pointer',
                                                        fontSize: 13,
                                                        backgroundColor: formData.selectedVehicle === vehicle.id
                                                            ? 'rgba(5, 136, 243, 0.15)'
                                                            : undefined,
                                                    }}
                                                    onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(5,136,243,0.1)'}
                                                    onMouseLeave={e => e.currentTarget.style.backgroundColor = formData.selectedVehicle === vehicle.id ? 'rgba(5,136,243,0.15)' : ''}
                                                >
                                                    {vehicle.reg}
                                                </li>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>

                    {/* Select Service Types */}
                    <div className="input-group">
                        <label>Service Types</label>
                        {/* <div className="checkbox-group">
                            {['PMI', 'BRAKES', 'MOT', 'TACHO'].map((serviceType) => (
                                <label key={serviceType} className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={formData.serviceTypes.includes(serviceType)}
                                        onChange={() => handleCheckboxChange(serviceType)}
                                    />
                                    {serviceType}
                                </label>
                            ))}
                        </div> */}
                        <div className="service-buttons-container">
                            {['PMI / BRAKES', 'MOT', 'TACHO', 'LOLER', 'REPAIR'].map((serviceType, index) => (
                                <button
                                    key={serviceType}
                                    className="service-button"
                                    style={{
                                        border: formData.serviceTypes.includes(serviceType) ? '2px solid blue' : '1px solid #ccc',
                                        backgroundColor:
                                            formData.serviceTypes.includes(serviceType)
                                            ? 'rgba(173, 216, 230, 0.4)'
                                            : theme === 'dark'
                                            ? 'rgba(255, 255, 255, 0.1)'
                                            : 'rgba(0, 0, 0, 0.1)',
                                        color: formData.serviceTypes.includes(serviceType) ? 'blue' : 'inherit',
                                    }}
                                    onClick={(e) => {
                                        e.preventDefault(); // Prevent the page from refreshing
                                        handleCheckboxChange(serviceType); // Toggle the selection
                                    }}
                                >
                                    {serviceType}
                                </button>
                            ))}
                        </div>

                    </div>

                    {/* Select Service Contact */}
                    <div className="input-group">
                        <label>Service Contact</label>
                        <select
                            name="selectedServiceContract"
                            value={formData.selectedServiceContract}
                            onChange={handleInputChange}
                            className="input"
                        >
                            <option value="">Select Service Contract</option>
                            {serviceContacts.map((contact) => (
                                <option key={contact.id} value={contact.id}>
                                    {contact.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    {errors && (
                        <div>
                            <span style={{ color: 'red' }}>{errors}</span>
                        </div>
                    )}
                </form>

                {/* Buttons */}
                <div className="button-group">
                    <button onClick={handleSubmit} className="submit-button">
                        Add Service
                    </button>
                    <button onClick={() => {
                        setRegSearch('');
                        setFormData({ selectedVehicle: '', serviceTypes: [], selectedServiceContract: '' });
                        onClose();
                    }} className="close-button">
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddServiceModal;
